import { Lock } from 'lucide-react';
import { ReactNode, useState } from 'react';
import type {
  AnyFieldSpecBase,
  ScopeSection as SchemaSection
} from 'v5/core/form-schema/formSchema.types';

type ScopeSection = SchemaSection<AnyFieldSpecBase>;
import { focusRings } from 'v5/design-system/_common/focusRing';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from 'v5/design-system/Collapsible';
import { SimpleTooltip } from 'v5/design-system/Tooltip';
import { OverriddenCountBadge } from './OverriddenCountBadge';
import { cn } from 'v5/platform/dom/cn';
import { useReadOnlyReasonLabel } from './useReadOnlyReasonLabel';
import { SECTION_ID_ATTRIBUTE } from './useSectionNavigation';

export interface ScopeSectionViewProps {
  section: ScopeSection;
  /** Optional icon rendered before the title (workspace icon). */
  icon: ReactNode;
  children: ReactNode;
  /** Always open, no trigger (e.g. a schema with only the Global section). */
  alwaysOpen?: boolean;
  /**
   * An always-open section without its header card: the content alone, when
   * naming the scope says nothing (the only section, no workspaces around).
   */
  showHeader?: boolean;
  /** Controlled open state (section navigation); uncontrolled when omitted. */
  open?: boolean;
  onOpenChange?(open: boolean): void;
  className?: string;
}

/**
 * Collapsible card for one scope section: Global, or one workspace. Spans the
 * form's width; the header and content keep the form gutter at the sides
 * (`SCHEMA_FORM_GUTTER`), so its card lines up with the content. The form lays out the
 * units inside: how far apart they sit depends on its mode.
 */
export function ScopeSectionView({
  section,
  icon,
  children,
  alwaysOpen = false,
  showHeader = true,
  open: openProp,
  onOpenChange,
  className
}: ScopeSectionViewProps) {
  const [openState, setOpenState] = useState(section.defaultExpanded);
  const readOnlyReasonLabel = useReadOnlyReasonLabel();
  const open = openProp ?? openState;
  const setOpen = (next: boolean) => {
    setOpenState(next);
    onOpenChange?.(next);
  };
  const readOnly = section.editability.type === 'read-only';

  // Header card as in the designs: quiet rounded surface, regular-weight
  // title, chevron on the far right (right when collapsed, up when open).
  const headerCardClassName =
    'rounded-xl bg-bg-subtle px-5 py-3.5 hover:no-underline focus-visible:no-underline';
  // Interactive only when collapsible: a darker surface on hover and the
  // design-system focus ring (the trigger drops the default underline).
  const headerTriggerClassName = cn(
    'cursor-pointer hover:bg-bg-inset/50',
    focusRings.focus.visible
  );
  const header = (
    <div className="flex min-w-0 flex-1 items-center gap-3 text-left">
      {icon}
      <span className="truncate text-lg font-medium text-fg-default">
        {section.title}
      </span>
      {section.overriddenCount > 0 && (
        <OverriddenCountBadge count={section.overriddenCount}>
          {section.overriddenCount} overridden
        </OverriddenCountBadge>
      )}
      {readOnly && (
        <SimpleTooltip
          title={readOnlyReasonLabel(
            section.editability.type === 'read-only'
              ? section.editability.reason
              : 'display-only',
            'resource'
          )}
        >
          <Lock className="size-4 shrink-0 text-fg-muted" />
        </SimpleTooltip>
      )}
    </div>
  );

  if (alwaysOpen) {
    return (
      <section
        data-testid={`schema-section-${section.id}`}
        {...{ [SECTION_ID_ATTRIBUTE]: section.id }}
        className={cn('flex flex-col gap-6', className)}
      >
        {showHeader && (
          <div className="flex flex-col px-(--schema-form-gutter)">
            <div className={cn('flex items-center', headerCardClassName)}>
              {header}
            </div>
          </div>
        )}
        <div className="px-(--schema-form-gutter)">
          <div className="px-2">{children}</div>
        </div>
      </section>
    );
  }

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      data-testid={`schema-section-${section.id}`}
      {...{ [SECTION_ID_ATTRIBUTE]: section.id }}
      className={cn('flex flex-col gap-2', className)}
    >
      <div className="flex flex-col px-(--schema-form-gutter)">
        <CollapsibleTrigger
          className={cn('gap-3', headerCardClassName, headerTriggerClassName)}
          chevron="right"
        >
          {header}
        </CollapsibleTrigger>
      </div>
      {/* `overflow-clip` instead of the primitive's `overflow-hidden`: it still
          clips the open/close animation but is no scroll container, so a
          unit's sticky editor header sticks to the form's scroll area. */}
      <CollapsibleContent className="overflow-clip px-(--schema-form-gutter)">
        <div className="px-2 pt-2">{children}</div>
      </CollapsibleContent>
    </Collapsible>
  );
}
